import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { HelpCircle } from 'lucide-react';
import { theme } from '../../styles/theme';

function getNestedValue(obj, path) {
  if (!path) return obj;
  const parts = path.split('.');
  let current = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = current[part];
  }
  return current;
}

function buildPreviewFromSchema(schema, path) {
  if (!schema) return null;
  if (!path) return schema;
  const parts = path.split('.');
  let current = schema;
  for (const part of parts) {
    if (!current || !current.properties) return null;
    current = current.properties[part];
    if (!current) return null;
    if (current.type === 'object' && current.properties) {
      continue;
    }
    if (current.type === 'array' && current.items) {
      return current;
    }
  }
  return current;
}

export default function ResultPathHelper({ value, onChange, payloadSchema }) {
  const { t } = useTranslation('campaigns');

  const EXAMPLES = [
    {
      id: 'root',
      label: t('resultPath.exampleRoot'),
      resultPath: '',
      json: [{ nome: 'João', telefone: '11999...' }],
    },
    {
      id: 'data-contacts',
      label: 'data.contacts',
      resultPath: 'data.contacts',
      json: { data: { contacts: [{ nome: 'Maria', telefone: '...' }] } },
    },
    {
      id: 'data-appointments',
      label: t('resultPath.exampleAppointments'),
      resultPath: 'data.appointments',
      json: { data: { appointments: [{ patientName: '...', phone: '...' }] } },
    },
  ];

  const activeExample = EXAMPLES.find((ex) => ex.resultPath === value);

  const previewExtracted = useMemo(() => {
    if (payloadSchema) {
      const sub = buildPreviewFromSchema(payloadSchema, value);
      if (sub) return sub;
    }
    if (activeExample) {
      return getNestedValue(activeExample.json, activeExample.resultPath);
    }
    return null;
  }, [value, payloadSchema, activeExample]);

  return (
    <Container>
      <LabelRow>
        <LabelText>{t('resultPath.label')}</LabelText>
        <Tooltip>
          <HelpCircle size={14} />
          <TooltipContent>
            {t('resultPath.tooltip')}
          </TooltipContent>
        </Tooltip>
      </LabelRow>

      <Input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('resultPath.placeholder')}
      />

      {!value && (
        <HintBox>
          {t('resultPath.rootHint')}
        </HintBox>
      )}

      <ExamplesLabel>{t('resultPath.examplesLabel')}</ExamplesLabel>
      <ExamplesGrid>
        {EXAMPLES.map((example) => (
          <ExampleCard
            key={example.id}
            $active={value === example.resultPath}
            onClick={() => onChange(example.resultPath)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onChange(example.resultPath);
              }
            }}
          >
            <ExampleLabel $active={value === example.resultPath}>
              {example.label}
            </ExampleLabel>
            <ExamplePath>
              resultPath: {example.resultPath ? `"${example.resultPath}"` : t('resultPath.empty')}
            </ExamplePath>
            <ExampleJson>
              {JSON.stringify(example.json, null, 2)}
            </ExampleJson>
          </ExampleCard>
        ))}
      </ExamplesGrid>

      {value && previewExtracted && (
        <PreviewSection>
          <PreviewLabel>{t('resultPath.previewLabel', { path: value })}</PreviewLabel>
          <PreviewJson>
            {JSON.stringify(previewExtracted, null, 2)}
          </PreviewJson>
        </PreviewSection>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const LabelText = styled.label`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;

const Tooltip = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  color: ${theme.colors.textMuted};
  cursor: help;

  &:hover > div {
    opacity: 1;
    visibility: visible;
  }
`;

const TooltipContent = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: ${theme.colors.gray800};
  color: ${theme.colors.white};
  font-size: ${theme.typography.xs};
  border-radius: ${theme.radii.md};
  white-space: normal;
  width: 240px;
  line-height: ${theme.typography.snug};
  opacity: 0;
  visibility: hidden;
  transition: ${theme.transitions.fast};
  z-index: ${theme.zIndex.dropdown};
  box-shadow: ${theme.shadows.md};
`;

const Input = styled.input`
  width: 100%;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  font-size: ${theme.typography.sm};
  font-family: ${theme.typography.fontMono};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  background: ${theme.colors.white};
  color: ${theme.colors.textPrimary};
  transition: ${theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primaryLight};
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const HintBox = styled.div`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: ${theme.colors.infoLight};
  border: 1px solid ${theme.colors.infoBorder};
  border-radius: ${theme.radii.md};
  font-size: ${theme.typography.xs};
  color: ${theme.colors.info};
`;

const ExamplesLabel = styled.div`
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.medium};
  color: ${theme.colors.textSecondary};
  margin-top: ${theme.spacing[2]};
`;

const ExamplesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: ${theme.spacing[3]};
`;

const ExampleCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]};
  border-radius: ${theme.radii.lg};
  border: 2px solid ${(p) => (p.$active ? theme.colors.primaryBorder : theme.colors.border)};
  background: ${(p) => (p.$active ? theme.colors.primaryLight : theme.colors.white)};
  cursor: pointer;
  transition: ${theme.transitions.fast};
  box-shadow: ${theme.shadows.sm};

  &:hover {
    border-color: ${(p) => (p.$active ? theme.colors.primary : theme.colors.borderHover)};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
  }
`;

const ExampleLabel = styled.div`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${(p) => (p.$active ? theme.colors.primary : theme.colors.textPrimary)};
`;

const ExamplePath = styled.div`
  font-size: ${theme.typography.xs};
  font-family: ${theme.typography.fontMono};
  color: ${theme.colors.textSecondary};
`;

const ExampleJson = styled.pre`
  margin: 0;
  padding: ${theme.spacing[2]};
  background: ${theme.colors.bgSecondary};
  border-radius: ${theme.radii.md};
  font-size: ${theme.typography.xs};
  font-family: ${theme.typography.fontMono};
  color: ${theme.colors.textPrimary};
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

const PreviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]};
  background: ${theme.colors.infoLight};
  border: 1px solid ${theme.colors.infoBorder};
  border-radius: ${theme.radii.lg};
`;

const PreviewLabel = styled.div`
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.medium};
  color: ${theme.colors.info};
`;

const PreviewJson = styled.pre`
  margin: 0;
  padding: ${theme.spacing[2]};
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: ${theme.typography.xs};
  font-family: ${theme.typography.fontMono};
  color: ${theme.colors.textPrimary};
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;
